import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Upload, FileText, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { DocType } from "@/types/proposals";

export default function UniversalDocImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [docType, setDocType] = useState<DocType>('proposal');
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const acceptedTypes = [
    '.xml',
    '.pdf',
    '.docx',
    '.jpg',
    '.jpeg',
    '.png',
    '.heic'
  ];

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (selectedFile: File) => {
    const extension = '.' + selectedFile.name.split('.').pop()?.toLowerCase();
    if (!acceptedTypes.includes(extension)) {
      toast.error("Unsupported file type. Please upload XML, PDF, DOCX, or image files.");
      return;
    }
    setFile(selectedFile);
  };

  const handleConvert = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setLoading(true);
    try {
      // Get current user's org
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('id', user.id)
        .single();

      if (!profile?.org_id) throw new Error("Organization not found");

      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      await new Promise((resolve, reject) => {
        reader.onload = resolve;
        reader.onerror = reject;
      });

      const base64Data = (reader.result as string).split(',')[1];

      // Call edge function
      const { data, error } = await supabase.functions.invoke('universal-doc-to-blenddoc', {
        body: {
          file_name: file.name,
          file_data: base64Data,
          doc_type: docType,
          org_id: profile.org_id
        }
      });

      if (error) throw error;

      if (data.template_id) {
        toast.success("Template imported successfully!");
        navigate(`/templates/builder/${data.template_id}`);
      } else {
        throw new Error(data.error || "Failed to import template");
      }
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(error.message || "Failed to import template");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Import Existing Templates</h1>
          <p className="text-muted-foreground">
            Upload XML, PDF, DOCX, or Images — RoofGenius converts them into editable digital templates instantly.
          </p>
        </div>

        <Card className="p-8">
          <div className="space-y-6">
            {/* File Upload Area */}
            <div>
              <Label className="mb-3 block">Upload Document</Label>
              <div
                className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                  dragActive
                    ? 'border-primary bg-primary/5'
                    : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {file ? (
                  <div>
                    <FileText className="w-16 h-16 mx-auto mb-4 text-primary" />
                    <p className="font-medium mb-2">{file.name}</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFile(null)}
                    >
                      Change File
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-lg font-medium mb-2">
                      Drag & Drop or Click to Upload
                    </p>
                    <p className="text-sm text-muted-foreground mb-4">
                      Accepts: XML, PDF, DOCX, JPG, PNG, HEIC
                    </p>
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      accept={acceptedTypes.join(',')}
                      onChange={handleFileInput}
                    />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('file-upload')?.click()}
                    >
                      Browse Files
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Document Type Selection */}
            <div>
              <Label className="mb-3 block">Document Type</Label>
              <RadioGroup value={docType} onValueChange={(value) => setDocType(value as DocType)}>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="proposal" id="proposal" />
                    <Label htmlFor="proposal" className="cursor-pointer">
                      Proposal
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="estimate" id="estimate" />
                    <Label htmlFor="estimate" className="cursor-pointer">
                      Estimate
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="contingency" id="contingency" />
                    <Label htmlFor="contingency" className="cursor-pointer">
                      Contingency Form
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="change_order" id="change_order" />
                    <Label htmlFor="change_order" className="cursor-pointer">
                      Change Order
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="supplement" id="supplement" />
                    <Label htmlFor="supplement" className="cursor-pointer">
                      Supplement
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Convert Button */}
            <Button
              onClick={handleConvert}
              disabled={!file || loading}
              className="w-full"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Converting Template...
                </>
              ) : (
                'Convert to BlendDoc Template'
              )}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
